/**
 * generateAuthorizationFormPdf
 *
 * Produces a filled-in version of the PD-920 "Authorization for Use and
 * Disclosure of Protected Health Information" form using pdfmake.  The output
 * mirrors the section headings, boilerplate text, checkbox layout, and
 * signature block from the original PDF attachment on the Jira ticket.
 */
import pdfMake from 'pdfmake/build/pdfmake'
import * as pdfFonts from 'pdfmake/build/vfs_fonts'
import type { Content, TDocumentDefinitions } from 'pdfmake/interfaces'
import type { Patient } from '@medplum/fhirtypes'
import { formatHumanName } from '@medplum/core'
import { urlToDataURL } from './pdfHelpers'
import { BRAND_BLUE } from './constants'
import { logError } from '../shared/errorUtils'
import type { AuthorizationFormState } from '../../AuthorizationRelease/authorizationFormData'
import {
  PURPOSE_OPTIONS,
  RECORD_TYPE_OPTIONS,
  SENSITIVE_TYPE_OPTIONS,
} from '../../AuthorizationRelease/authorizationFormData'
import logo from '/logo.png'

// ── pdfmake VFS bootstrap ────────────────────────────────────────────────────
const vfs =
  (pdfFonts as Record<string, unknown>).vfs ||
  (pdfFonts as Record<string, Record<string, unknown>>).pdfMake?.vfs
if (vfs) {
  pdfMake.vfs = vfs as Record<string, string>
}

// ── Formatting helpers ───────────────────────────────────────────────────────

/** Convert YYYY-MM-DD (HTML date input) to MM/DD/YYYY, avoiding TZ shifts. */
function fmtDate(val: string): string {
  if (!val) return ''
  const d = new Date(val.length === 10 ? `${val}T00:00:00` : val)
  return isNaN(d.getTime())
    ? val
    : d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

// ── pdfmake block builders ───────────────────────────────────────────────────

/**
 * Canvas-drawn checkbox (empty rect or rect with X) followed by a label.
 * Uses canvas shapes to avoid Unicode / font-support issues.
 */
function cb(checked: boolean, label: string): Content {
  const s = 7 // box size in points
  // Build canvas items as plain objects; pdfmake accepts these at runtime.

  const canvasItems: any[] = [
    { type: 'rect', x: 0, y: 2, w: s, h: s, lineWidth: 0.5, lineColor: '#000' },
  ]
  if (checked) {
    canvasItems.push(
      { type: 'line', x1: 1, y1: 3.5, x2: s - 1, y2: s + 1.5, lineWidth: 0.8, lineColor: '#000' },
      { type: 'line', x1: s - 1, y1: 3.5, x2: 1, y2: s + 1.5, lineWidth: 0.8, lineColor: '#000' },
    )
  }
  return {
    columns: [
      { canvas: canvasItems, width: s + 6 },
      { text: label, fontSize: 9, margin: [2, 0, 0, 0] as [number, number, number, number] },
    ],
    columnGap: 0,
    margin: [0, 1, 0, 2] as [number, number, number, number],
  }
}

/** Labeled field: label text + bold value + a thin underline. */
function fld(label: string, value: string, lineLen = 430): Content {
  return {
    margin: [0, 1, 0, 5] as [number, number, number, number],
    stack: [
      {
        text: [
          { text: `${label}: `, fontSize: 9, color: '#555' },
          { text: value, fontSize: 9, bold: true },
        ],
      },
      {
        canvas: [
          { type: 'line', x1: 0, y1: 0, x2: lineLen, y2: 0, lineWidth: 0.4, lineColor: '#bbb' },
        ] as any[],
      },
    ],
  }
}

/** Bold section heading with top spacing. */
function heading(text: string): Content {
  return {
    text,
    fontSize: 10,
    bold: true,
    margin: [0, 8, 0, 4] as [number, number, number, number],
  }
}

/** Small body / boilerplate paragraph. */
function para(text: string): Content {
  return {
    text,
    fontSize: 9,
    color: '#333',
    margin: [0, 0, 0, 4] as [number, number, number, number],
  }
}

// ── Main generator ───────────────────────────────────────────────────────────

/**
 * Generates a filled-in authorization form PDF that matches the layout of
 * the PD-920 attachment.  Returns raw PDF bytes.
 */
export async function generateAuthorizationFormPdf(
  patient: Patient,
  form: AuthorizationFormState,
  signatureDataUrl: string,
  signatureDate: string,
): Promise<Uint8Array> {
  let logoDataUrl = ''
  try {
    logoDataUrl = await urlToDataURL(logo)
  } catch (err) {
    logError('BeHuman logo fetch', err)
  }

  // Patient display values
  const fullName = patient.name?.[0] ? formatHumanName(patient.name[0]) : ''
  const dob = fmtDate(patient.birthDate ?? '')
  const phone = patient.telecom?.find((t) => t.system === 'phone')?.value ?? ''
  const addr0 = patient.address?.find((a) => a.use !== 'old')
  const address = addr0
    ? [addr0.line?.join(' '), addr0.city, addr0.state, addr0.postalCode].filter(Boolean).join(', ')
    : ''

  // Facility display values — no "Phone:" / "Fax:" prefix since the field label already says Phone/Fax
  const facilityFaxPhone = [form.facility.phone, form.facility.fax].filter(Boolean).join('  |  ')
  const facilityAddr = [
    form.facility.street,
    form.facility.city,
    form.facility.state,
    form.facility.zip,
  ]
    .filter(Boolean)
    .join(', ')

  const content: Content[] = []

  // ── Header (logo + beHuman letterhead on the left, then centered title) ──────
  // Mirrors the requisition fax letterhead (behumanPdfGenerator): logo + org
  // name (brand blue) + fax + address, left-aligned beside the logo. No phone.
  const senderLetterhead: Content = {
    stack: [
      {
        text: 'BeHuman Health',
        fontSize: 9,
        bold: true,
        color: BRAND_BLUE,
        margin: [0, 0, 0, 2] as [number, number, number, number],
      },
      {
        text: 'Fax: (561) 821-9872',
        fontSize: 7,
        bold: true,
        color: '#555',
        margin: [0, 0, 0, 1] as [number, number, number, number],
      },
      {
        text: '2500 E Hallandale Beach Blvd, Suite 400, Hallandale Beach, FL, 33009, United States',
        fontSize: 7,
        color: '#555',
      },
    ],
    margin: [8, 0, 0, 0] as [number, number, number, number],
  }
  content.push({
    columns: [
      logoDataUrl
        ? {
            image: logoDataUrl,
            width: 60,
            margin: [0, 0, 15, 0] as [number, number, number, number],
          }
        : { text: '', width: 60 },
      senderLetterhead,
    ],
    margin: [0, 0, 0, 6] as [number, number, number, number],
  })
  content.push({
    text: 'AUTHORIZATION FOR USE AND DISCLOSURE OF PROTECTED HEALTH INFORMATION',
    fontSize: 12,
    bold: true,
    alignment: 'center',
    margin: [0, 6, 0, 10] as [number, number, number, number],
  })

  // ── Patient Information ─────────────────────────────────────────────────────
  content.push(heading('Patient Information'))
  content.push(fld('Patient Full Name', fullName))
  content.push(fld('Date of Birth', dob))
  content.push(fld('Phone Number', phone))
  content.push(fld('Address', address))
  // ── Authorization to Release Records ───────────────────────────────────────
  content.push(heading('Authorization to Release Records'))
  content.push(
    para(
      'I authorize the following healthcare providers, facilities, laboratories, imaging centers, hospitals, pharmacies, health information exchanges, and other healthcare entities to disclose my protected health information:',
    ),
  )
  content.push(fld('Facility/Provider Name', form.facility.name))
  content.push(fld('Phone/Fax (if known)', facilityFaxPhone))
  content.push(fld('Address (if known)', facilityAddr))
  // ── Authorized Recipient ────────────────────────────────────────────────────
  content.push(heading('Authorized Recipient'))
  content.push(para('I authorize disclosure of my records to:'))
  content.push({
    text: 'beHuman Medical Group PC and its affiliated treating providers, clinicians, contracted care coordinators, affiliated provider organizations, and authorized business associates for purposes of treatment, care coordination, cancer screening, population health management, quality improvement, healthcare operations, and continuity of care.',
    fontSize: 9,
    bold: true,
    margin: [0, 0, 0, 6] as [number, number, number, number],
  })
  content.push({
    text: 'Recipient Contact Information:',
    fontSize: 9,
    margin: [0, 0, 0, 2] as [number, number, number, number],
  })
  content.push({
    text: 'beHuman Medical PC\nPhone: 866-BEHUMAN (866-234-8626)\nFax: (561) 821-9872\nAddress: 2500 E Hallandale Beach Blvd, Suite 400, Hallandale Beach, FL, 33009, United States',
    fontSize: 9,
    color: '#333',
  })
  // ── Purpose of Disclosure ───────────────────────────────────────────────────
  content.push(heading('Purpose of Disclosure'))
  content.push(para('The purpose of this disclosure is:'))
  for (const opt of PURPOSE_OPTIONS) {
    const isOther = opt === 'Other'
    const checked = isOther ? form.purpose === 'Other' : form.purpose === opt
    const label =
      isOther && form.purpose === 'Other' && form.purposeOther
        ? `Other: ${form.purposeOther}`
        : isOther
          ? 'Other: _______________________________________'
          : opt
    content.push(cb(checked, label))
  }
  // ── Information Authorized for Release ─────────────────────────────────────
  content.push(heading('Information Authorized for Release'))
  content.push(
    para(
      'I authorize the release of the following records, including electronic records, laboratory results, imaging, reports, and clinical documentation:',
    ),
  )
  // Two-column layout for the 13 record types (7 left, 6 right)
  const recordCbs = RECORD_TYPE_OPTIONS.map((opt) => {
    const isOther = opt === 'Other'
    const checked = isOther ? form.recordTypes.includes('Other') : form.recordTypes.includes(opt)
    const label =
      isOther && form.recordTypes.includes('Other') && form.recordTypesOther
        ? `Other: ${form.recordTypesOther}`
        : isOther
          ? 'Other: _______________________________________'
          : opt
    return cb(checked, label)
  })
  const half = Math.ceil(recordCbs.length / 2)
  content.push({
    columns: [
      { stack: recordCbs.slice(0, half), width: '50%' },
      { stack: recordCbs.slice(half), width: '50%' },
    ],
    margin: [0, 0, 0, 4] as [number, number, number, number],
  })
  content.push({
    text: 'Date Range of Records Requested:',
    fontSize: 9,
    margin: [0, 4, 0, 2] as [number, number, number, number],
  })
  content.push({
    columns: [
      { stack: [fld('From', fmtDate(form.dateRangeStart), 200)], width: '50%' },
      { stack: [fld('To', fmtDate(form.dateRangeEnd), 200)], width: '50%' },
    ],
  })
  // ── Sensitive Information ───────────────────────────────────────────────────
  content.push(heading('Sensitive Information'))
  content.push(
    para('I specifically authorize the release of the following information if applicable:'),
  )
  for (const opt of SENSITIVE_TYPE_OPTIONS) {
    content.push(cb(form.sensitiveTypes.includes(opt), opt))
  }

  // ── Right to Revoke ─────────────────────────────────────────────────────────
  content.push(heading('Right to Revoke'))
  content.push(para('I understand that:'))
  content.push({
    ul: [
      'I may revoke this authorization at any time in writing, except to the extent action has already been taken in reliance upon it.',
      'Treatment, payment, enrollment, or eligibility for benefits may not be conditioned upon signing this authorization unless permitted by law.',
      'Information disclosed pursuant to this authorization may be subject to redisclosure by the recipient and may no longer be protected by HIPAA.',
    ],
    fontSize: 9,
    color: '#333',
    margin: [0, 0, 0, 6] as [number, number, number, number],
  })
  // ── Patient Signature ───────────────────────────────────────────────────────
  content.push(heading('Patient Signature'))
  content.push(fld('Patient Name', fullName))

  if (signatureDataUrl) {
    content.push({
      margin: [0, 8, 0, 0] as [number, number, number, number],
      table: {
        widths: [200, 20, 150],
        body: [
          [
            { image: signatureDataUrl, width: 160, height: 40 },
            { text: '' },
            {
              text: signatureDate,
              fontSize: 9,
              margin: [0, 26, 0, 0] as [number, number, number, number],
            },
          ],
          [
            {
              canvas: [
                { type: 'line', x1: 0, y1: 0, x2: 190, y2: 0, lineWidth: 0.5, lineColor: '#000' },
              ] as any[],
            },
            { text: '' },
            {
              canvas: [
                { type: 'line', x1: 0, y1: 0, x2: 140, y2: 0, lineWidth: 0.5, lineColor: '#000' },
              ] as any[],
            },
          ],
          [
            { text: 'Patient Signature', fontSize: 7, color: '#666' },
            { text: '' },
            { text: 'Date', fontSize: 7, color: '#666' },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingTop: () => 1,
        paddingBottom: () => 1,
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    })
  } else {
    content.push({
      text: 'Signature pending — patient has not yet signed this authorization.',
      fontSize: 9,
      italics: true,
      color: '#888',
      margin: [0, 4, 0, 10] as [number, number, number, number],
    })
    content.push({
      table: {
        widths: [200, 20, 150],
        body: [
          [
            {
              canvas: [
                { type: 'line', x1: 0, y1: 0, x2: 190, y2: 0, lineWidth: 0.5, lineColor: '#aaa' },
              ] as any[],
              margin: [0, 18, 0, 0] as [number, number, number, number],
            },
            { text: '' },
            {
              canvas: [
                { type: 'line', x1: 0, y1: 0, x2: 140, y2: 0, lineWidth: 0.5, lineColor: '#aaa' },
              ] as any[],
              margin: [0, 18, 0, 0] as [number, number, number, number],
            },
          ],
          [
            { text: 'Patient Signature', fontSize: 7, color: '#aaa' },
            { text: '' },
            { text: 'Date', fontSize: 7, color: '#aaa' },
          ],
        ],
      },
      layout: {
        hLineWidth: () => 0,
        vLineWidth: () => 0,
        paddingTop: () => 1,
        paddingBottom: () => 1,
        paddingLeft: () => 0,
        paddingRight: () => 0,
      },
    })
  }

  const docDef: TDocumentDefinitions = {
    pageSize: 'LETTER',
    // Extra bottom margin reserves room for the HIPAA notice footer.
    pageMargins: [40, 40, 40, 70],
    content,
    // HIPAA compliance notice as a true page footer — bottom of EVERY page,
    // never floating inline between sections.
    footer: () => ({
      text: 'This authorization complies with applicable federal privacy regulations under the Health Insurance Portability and Accountability Act ("HIPAA"), 45 CFR Parts 160 and 164. A copy or electronic version of this authorization shall be considered as valid as the original.',
      fontSize: 7,
      color: '#555',
      margin: [40, 8, 40, 0] as [number, number, number, number],
    }),
    defaultStyle: { font: 'Roboto', fontSize: 9 },
  }

  return new Promise((resolve, reject) => {
    pdfMake.createPdf(docDef).getBuffer((buffer: Uint8Array) => {
      if (buffer) resolve(buffer)
      else reject(new Error('pdfmake returned empty buffer'))
    })
  })
}
